import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { AuthService } from './auth.service';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class ApiService {
  constructor(
    private http: HttpClient,
    private authService: AuthService
  ) {}

  private getHeaders(): HttpHeaders {
    const token = this.authService.getToken();
    return new HttpHeaders({
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    });
  }

  // Categories
  getCategories(page?: number, limit?: number, search?: string, status?: string): Observable<any> {
    let url = `${environment.apiUrl}/categories?`;
    const params: string[] = [];
    // Always send page and limit parameters for pagination
    const pageNum = page !== undefined && page !== null ? page : 1;
    const limitNum = limit !== undefined && limit !== null ? limit : 5;
    params.push(`page=${pageNum}`);
    params.push(`limit=${limitNum}`);
    // Only add search if it's not empty
    if (search && search.trim() !== '') {
      params.push(`search=${encodeURIComponent(search.trim())}`);
    }
    // Only add status if it's not empty and not undefined
    if (status && status.trim() !== '') {
      params.push(`status=${status.trim()}`);
    }
    url += params.join('&');
    return this.http.get(url, { headers: this.getHeaders() });
  }

  getCategory(id: string): Observable<any> {
    return this.http.get(`${environment.apiUrl}/categories/${id}`, { headers: this.getHeaders() });
  }

  createCategory(data: any): Observable<any> {
    // Send JSON with image key (URL is already uploaded to S3)
    const categoryData = {
      name: data.name,
      description: data.description,
      image: data.image || '',
      suppliers: data.suppliers || [] // Include supplier IDs array
    };

    return this.http.post(`${environment.apiUrl}/categories`, categoryData, { headers: this.getHeaders() });
  }

  updateCategory(id: string, data: any): Observable<any> {
    // Send JSON with image key (URL is already uploaded to S3)
    const categoryData: any = {};
    if (data.name !== undefined) categoryData.name = data.name;
    if (data.description !== undefined) categoryData.description = data.description;
    if (data.image !== undefined) categoryData.image = data.image;
    if (data.suppliers !== undefined) categoryData.suppliers = data.suppliers || []; // Include supplier IDs array

    return this.http.put(`${environment.apiUrl}/categories/${id}`, categoryData, { headers: this.getHeaders() });
  }

  deleteCategoryImage(id: string): Observable<any> {
    return this.http.delete(`${environment.apiUrl}/categories/${id}/image`, { headers: this.getHeaders() });
  }

  updateCategoryStatus(id: string, isActive: boolean): Observable<any> {
    return this.http.patch(`${environment.apiUrl}/categories/${id}/status`, { isActive }, { headers: this.getHeaders() });
  }

  deleteCategory(id: string): Observable<any> {
    return this.http.delete(`${environment.apiUrl}/categories/${id}`, { headers: this.getHeaders() });
  }

  getCategoriesBySupplier(supplierId: string): Observable<any> {
    return this.http.get(`${environment.apiUrl}/categories/supplier/${supplierId}`, { headers: this.getHeaders() });
  }

  // Products
  getProducts(page?: number, limit?: number, search?: string, status?: string, categoryId?: string): Observable<any> {
    let url = `${environment.apiUrl}/products?`;
    const params: string[] = [];
    
    // Always send page and limit parameters for pagination
    const pageNum = page !== undefined && page !== null ? page : 1;
    const limitNum = limit !== undefined && limit !== null ? limit : 5;
    params.push(`page=${pageNum}`);
    params.push(`limit=${limitNum}`);
    
    if (search) params.push(`search=${encodeURIComponent(search)}`);
    if (status) params.push(`status=${status}`);
    if (categoryId) params.push(`category=${categoryId}`);
    
    url += params.join('&');
    return this.http.get(url, { headers: this.getHeaders() });
  }

  getProduct(id: string): Observable<any> {
    return this.http.get(`${environment.apiUrl}/products/${id}`, { headers: this.getHeaders() });
  }

  getProductDetails(id: string): Observable<any> {
    return this.http.get(`${environment.apiUrl}/products/${id}/details`, { headers: this.getHeaders() });
  }

  checkProductName(name: string): Observable<any> {
    return this.http.get(`${environment.apiUrl}/products/check-name`, 
      {
        headers: this.getHeaders(),
        params: { name: name }  // Angular automatically encode karega
      }
    );
  }

  createProduct(data: any): Observable<any> {
    return this.http.post(`${environment.apiUrl}/products`, data, { headers: this.getHeaders() });
  }

  updateProduct(id: string, data: any): Observable<any> {
    return this.http.put(`${environment.apiUrl}/products/${id}`, data, { headers: this.getHeaders() });
  }

  updateProductStatus(id: string, isActive: boolean): Observable<any> {
    return this.http.patch(`${environment.apiUrl}/products/${id}/status`, { isActive }, { headers: this.getHeaders() });
  }

  deleteProduct(id: string): Observable<any> {
    return this.http.delete(`${environment.apiUrl}/products/${id}`, { headers: this.getHeaders() });
  }

  // Users
  getUsers(userType?: string): Observable<any> {
    const url = userType 
      ? `${environment.apiUrl}/users?userType=${userType}`
      : `${environment.apiUrl}/users`;
    return this.http.get(url, { headers: this.getHeaders() });
  }

  getUserById(id: string): Observable<any> {
    return this.http.get(`${environment.apiUrl}/users/${id}`, { headers: this.getHeaders() });
  }

  getSuppliers(page?: number, limit?: number, search?: string, status?: string, country?: string): Observable<any> {
    let url = `${environment.apiUrl}/users/suppliers?`;
    const params: string[] = [];
    // Always send page and limit parameters - use explicit values
    const pageNum = page !== undefined && page !== null ? page : 1;
    const limitNum = limit !== undefined && limit !== null ? limit : 5;
    params.push(`page=${pageNum}`);
    params.push(`limit=${limitNum}`);
    if (search) params.push(`search=${encodeURIComponent(search)}`);
    if (status) params.push(`status=${status}`);
    if (country) params.push(`country=${encodeURIComponent(country)}`);
    url += params.join('&');
    return this.http.get(url, { headers: this.getHeaders() });
  }

  getSupplierStats(): Observable<any> {
    return this.http.get(`${environment.apiUrl}/users/suppliers/stats`, { headers: this.getHeaders() });
  }

  getProcurementManagers(): Observable<any> {
    return this.http.get(`${environment.apiUrl}/users/procurement-managers`, { headers: this.getHeaders() });
  }

  createUser(userData: any): Observable<any> {
    return this.http.post(`${environment.apiUrl}/users`, userData, { headers: this.getHeaders() });
  }

  updateUser(id: string, userData: any): Observable<any> {
    return this.http.put(`${environment.apiUrl}/users/${id}`, userData, { headers: this.getHeaders() });
  }

  updateUserStatus(id: string, isActive: boolean): Observable<any> {
    return this.http.patch(`${environment.apiUrl}/users/${id}/status`, { isActive }, { headers: this.getHeaders() });
  }

  deleteUser(id: string): Observable<any> {
    return this.http.delete(`${environment.apiUrl}/users/${id}`, { headers: this.getHeaders() });
  }

  resetPassword(newPassword: string): Observable<any> {
    return this.http.patch(`${environment.apiUrl}/users/reset-password`, 
      { newPassword }, 
      { headers: this.getHeaders() }
    );
  }

  // Quotes
  getQuotes(status?: string, productId?: string, supplierId?: string, page?: number, limit?: number, search?: string): Observable<any> {
    let url = `${environment.apiUrl}/quotes?`;
    const params: string[] = [];
    if (status) params.push(`status=${encodeURIComponent(status)}`);
    if (productId) params.push(`product=${productId}`);
    if (supplierId) params.push(`supplier=${supplierId}`);
    if (page) params.push(`page=${page}`);
    if (limit) params.push(`limit=${limit}`);
    if (search) params.push(`search=${encodeURIComponent(search)}`);
    url += params.length > 0 ? params.join('&') : '';
    return this.http.get(url, { headers: this.getHeaders() });
  }

  getQuote(id: string): Observable<any> {
    return this.http.get(`${environment.apiUrl}/quotes/${id}`, { headers: this.getHeaders() });
  }

  getQuotesByProduct(productId: string): Observable<any> {
    return this.http.get(`${environment.apiUrl}/quotes/product/${productId}`, { headers: this.getHeaders() });
  }

  getQuotesBySupplier(supplierId: string, page?: number, limit?: number, search?: string, status?: string, category?: string): Observable<any> {
    let url = `${environment.apiUrl}/quotes/supplier/${supplierId}?`;
    const params: string[] = [];
    if (page) params.push(`page=${page}`);
    if (limit) params.push(`limit=${limit}`);
    if (search) params.push(`search=${encodeURIComponent(search)}`);
    if (status) params.push(`status=${status}`);
    if (category) params.push(`category=${category}`);
    url += params.join('&');
    return this.http.get(url, { headers: this.getHeaders() });
  }

  getLeads(): Observable<any> {
    return this.http.get(`${environment.apiUrl}/quotes/leads`, { headers: this.getHeaders() });
  }

  getActiveQuotes(page?: number, limit?: number, search?: string): Observable<any> {
    let url = `${environment.apiUrl}/quotes/active?`;
    const params: string[] = [];
    if (page) params.push(`page=${page}`);
    if (limit) params.push(`limit=${limit}`);
    if (search) params.push(`search=${encodeURIComponent(search)}`);
    url += params.length > 0 ? params.join('&') : '';
    return this.http.get(url, { headers: this.getHeaders() });
  }

  createQuote(data: any): Observable<any> {
    return this.http.post(`${environment.apiUrl}/quotes`, data, { headers: this.getHeaders() });
  }

  updateQuote(id: string, data: any): Observable<any> {
    return this.http.put(`${environment.apiUrl}/quotes/${id}`, data, { headers: this.getHeaders() });
  }

  updateQuoteStatus(id: string, status: string): Observable<any> {
    return this.http.patch(`${environment.apiUrl}/quotes/${id}/status`, { status }, { headers: this.getHeaders() });
  }

  deleteQuote(id: string): Observable<any> {
    return this.http.delete(`${environment.apiUrl}/quotes/${id}`, { headers: this.getHeaders() });
  }

  unshareQuote(id: string): Observable<any> {
    return this.http.patch(`${environment.apiUrl}/quotes/${id}/unshare`, {}, { headers: this.getHeaders() });
  }

  reshareQuote(id: string): Observable<any> {
    return this.http.patch(`${environment.apiUrl}/quotes/${id}/reshare`, {}, { headers: this.getHeaders() });
  }

  // Category Sharing
  shareCategory(data: any): Observable<any> {
    return this.http.post(`${environment.apiUrl}/quotes/share-category`, data, { headers: this.getHeaders() });
  }

  getSharedCategories(): Observable<any> {
    return this.http.get(`${environment.apiUrl}/quotes/shared-categories`, { headers: this.getHeaders() });
  }

  // Image Upload Services
  /**
   * Upload single image
   * @param file - File object to upload
   * @param folder - Optional folder name in S3 (default: 'uploads')
   * @returns Observable with image data including URL
   */
  uploadImage(file: File, folder?: string): Observable<any> {
    const formData = new FormData();
    formData.append('file', file);

    const token = this.authService.getToken();
    const headers = new HttpHeaders({
      'Authorization': `Bearer ${token}`
      // Don't set Content-Type, let browser set it with boundary for FormData
    });

    const url = folder 
      ? `${environment.apiUrl}/images/upload?folder=${folder}`
      : `${environment.apiUrl}/images/upload`;

    return this.http.post(url, formData, { headers });
  }

  /**
   * Upload multiple images
   * @param files - Array of File objects to upload
   * @param folder - Optional folder name in S3 (default: 'uploads')
   * @param maxCount - Optional maximum number of files (default: 10)
   * @returns Observable with array of image data including URLs
   */
  uploadMultipleImages(files: File[], folder?: string, maxCount?: number): Observable<any> {
    const formData = new FormData();
    files.forEach(file => {
      formData.append('files', file);
    });

    const token = this.authService.getToken();
    const headers = new HttpHeaders({
      'Authorization': `Bearer ${token}`
    });

    let url = `${environment.apiUrl}/images/upload-multiple`;
    const params: string[] = [];
    if (folder) params.push(`folder=${folder}`);
    if (maxCount) params.push(`maxCount=${maxCount}`);
    if (params.length > 0) url += '?' + params.join('&');

    return this.http.post(url, formData, { headers });
  }

  /**
   * Get image URL from key
   * @param key - S3 image key
   * @returns Observable with image URL
   */
  getImageUrl(key: string): Observable<any> {
    return this.http.get(`${environment.apiUrl}/images/url/${encodeURIComponent(key)}`, { headers: this.getHeaders() });
  }

  /**
   * Delete single image
   * @param key - S3 image key or full URL
   * @returns Observable with delete result
   */
  deleteImage(key: string): Observable<any> {
    return this.http.delete(`${environment.apiUrl}/images/${encodeURIComponent(key)}`, { headers: this.getHeaders() });
  }

  /**
   * Delete multiple images
   * @param keys - Array of S3 image keys or full URLs
   * @returns Observable with delete results
   */
  deleteMultipleImages(keys: string[]): Observable<any> {
    return this.http.delete(`${environment.apiUrl}/images/delete-multiple`, {
      headers: this.getHeaders(),
      body: { keys }
    });
  }

  /**
   * Update image (delete old and upload new)
   * @param file - New File object to upload
   * @param oldKey - Old S3 image key or full URL to delete
   * @param folder - Optional folder name in S3 (default: 'uploads')
   * @returns Observable with new image data including URL
   */
  updateImage(file: File, oldKey: string, folder?: string): Observable<any> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('oldKey', oldKey);

    const token = this.authService.getToken();
    const headers = new HttpHeaders({
      'Authorization': `Bearer ${token}`
    });

    const url = folder 
      ? `${environment.apiUrl}/images/update?folder=${folder}`
      : `${environment.apiUrl}/images/update`;

    return this.http.put(url, formData, { headers });
  }

  /**
   * Upload specification file (ai, cdr, pdf, png, jpeg, jpg, xlsx, csv)
   * @param file - File object to upload
   * @param folder - Optional folder name in S3 (default: 'specifications')
   * @returns Observable with file data including URL
   */
  uploadSpecificationFile(file: File, folder?: string): Observable<any> {
    const formData = new FormData();
    formData.append('file', file);

    const token = this.authService.getToken();
    const headers = new HttpHeaders({
      'Authorization': `Bearer ${token}`
      // Don't set Content-Type, let browser set it with boundary for FormData
    });

    const url = folder 
      ? `${environment.apiUrl}/images/upload-specification?folder=${folder}`
      : `${environment.apiUrl}/images/upload-specification`;

    return this.http.post(url, formData, { headers });
  }
}


